import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function formatApiError(detail) {
  if (detail == null) return 'Qualcosa è andato storto';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => {
      if (!e) return '';
      if (typeof e === 'string') return e;
      const field = Array.isArray(e.loc) ? e.loc.slice(-1)[0] : '';
      const msg = typeof e.msg === 'string' ? e.msg : JSON.stringify(e);
      return field ? `${field}: ${msg}` : msg;
    }).filter(Boolean).join(' • ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const registerProprietario = async (payload) => {
    try {
      const { data } = await axios.post(`${API}/auth/register/proprietario`, payload, { withCredentials: true });
      // OTP flow: no auto-login. Caller handles UI to enter OTP.
      return { success: true, data, requires_verification: data.requires_verification };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const registerImpresa = async (payload) => {
    try {
      const { data } = await axios.post(`${API}/auth/register/impresa`, payload, { withCredentials: true });
      return { success: true, data, requires_verification: data.requires_verification };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const verifyOtp = async (email, otp) => {
    try {
      const { data } = await axios.post(`${API}/auth/verify-otp`, { email, otp }, { withCredentials: true });
      setUser(data);
      return { success: true, data };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const resendOtp = async (email) => {
    try {
      await axios.post(`${API}/auth/resend-otp`, { email });
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      return { success: true, data };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(false);
    } catch (e) { console.error(e); }
  };

  const stopImpersonating = async () => {
    try {
      await axios.post(`${API}/admin/stop-impersonate`, {}, { withCredentials: true });
      await checkAuth();
      return true;
    } catch (e) { return false; }
  };

  const value = useMemo(
    () => ({ user, loading, registerProprietario, registerImpresa, verifyOtp, resendOtp, login, logout, checkAuth, stopImpersonating }),
    [user, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
